import React, { useEffect, useState, useRef, memo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EnhancedProductCard } from './EnhancedProductCard';
import { ArrowRightIcon } from 'lucide-react';
import { useHorizontalScroll } from '../../lib/hooks/useHorizontalScroll';
import { haptics } from '../../lib/utils/haptics';
interface ProductsShowcaseSectionProps {
  title: string;
  subtitle?: string;
  backgroundColor: string;
  products: any[];
  icon?: React.ReactNode;
  showViewAll?: boolean;
  viewAllLink?: string;
  countdown?: {
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  };
}
export const ProductsShowcaseSection = memo(function ProductsShowcaseSection({
  title,
  subtitle,
  backgroundColor,
  products,
  icon,
  viewAllLink
}: ProductsShowcaseSectionProps) {
  const [searchParams] = useSearchParams();
  const isNowContext = searchParams.get('from') === 'now';
  const [currentSlide, setCurrentSlide] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  const totalSlides = Math.ceil(products.length / (isNowContext ? 3 : 2));
  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const scrollLeft = scrollContainerRef.current.scrollLeft;
      const slideWidth = scrollContainerRef.current.offsetWidth;
      const newSlide = Math.round(scrollLeft / slideWidth);
      setCurrentSlide(newSlide);
    }
  };
  const scrollToSlide = (index: number) => {
    if (scrollContainerRef.current) {
      const slideWidth = scrollContainerRef.current.offsetWidth;
      scrollContainerRef.current.scrollTo({
        left: slideWidth * index,
        behavior: 'smooth'
      });
    }
  };
  return <div className="w-full" data-id="element-6364" data-mp="jm1y7484dyk7">
      <div className={`${backgroundColor} rounded-xl overflow-hidden mb-6`} data-id="element-6365" data-mp="bizptzrga02t">
        <div className="p-6 md:p-8" data-id="element-6366" data-mp="7hxnvb65f9x5">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center" data-id="element-6367" data-mp="7izbsg89jqwa">
            <div className="mb-4 md:mb-0" data-id="element-6368" data-mp="liqj5y49y91b">
              {subtitle && <span className="text-xs font-bold text-white bg-white bg-opacity-20 px-2 py-0.5 rounded-full mb-2 inline-block uppercase" data-id="element-6369" data-mp="givw65nme3lj">
                  {subtitle}
                </span>}
              <h2 className="text-2xl md:text-3xl font-bold text-white flex items-center" data-id="element-6370" data-mp="qhbm45awv6tg">
                {icon && <span className="mr-2" data-id="element-6371" data-mp="148q7ujw7dm47">{icon}</span>}
                {title}
              </h2>
            </div>
            <a href={viewAllLink} className="text-white bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center flex-shrink-0" data-id="element-6372" data-mp="za53q9dtyiah">
              View All
              <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-6373" data-mp="1st3ao024mf4d" />
            </a>
          </div>
        </div>
      </div>
      {/* Mobile Slider */}
      {isMobile ? <div className="relative" data-id="element-6374" data-mp="1wu4zro2bxasm">
          <div ref={scrollContainerRef} onScroll={handleScroll} className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide gap-3" style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch'
      }} data-id="element-6375" data-mp="1iuvvnmree750">
            {products.map(product => <div key={product.id} className="snap-start flex-shrink-0" style={{
          width: isNowContext ? 'calc(33.333% - 8px)' : 'calc(50% - 6px)'
        }} data-id="element-6376" data-mp="1dxnd3l2c8138">
                <EnhancedProductCard product={product} data-id="element-6377" data-mp="q79ibmsdbo4y" />
              </div>)}
          </div>
          {/* Pagination Dots */}
          <div className="flex justify-center gap-2 mt-4" data-id="element-6378" data-mp="v7fbj7eadip5">
            {Array.from({
          length: totalSlides
        }).map((_, index) => <button key={index} onClick={() => scrollToSlide(index)} className={`h-2 rounded-full transition-all ${currentSlide === index ? 'w-6 bg-[#1E2B55]' : 'w-2 bg-gray-300'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-6379" data-mp="1ti6stjqcxlzn" data-mpi={index} />)}
          </div>
        </div> :
    // Desktop Grid
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4" data-id="element-6380" data-mp="1aq4jyujyj9zy">
          {products.map(product => <EnhancedProductCard key={product.id} product={product} data-id="element-6381" data-mp="15scip9xfabj3" />)}
        </div>}
    </div>;
});